import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  FormControl,
  MenuItem,
  Typography,
  Grid,
  CircularProgress,
} from "@mui/material";
import moment from "moment";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useDispatch, useSelector } from "react-redux";
import { userSelector } from "../../../user-redux/selector";
import {
  interviewScheduleAction,
  creteNotificationAction,
} from "../../redux/action";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { myCandidatesSelector } from "../../redux/selector";

// Interview Schedule
const Step6 = () => {
  const dispatch = useDispatch();

  const { candidateDetails, jobDetails, selectedSubmission } =
    useSelector(myCandidatesSelector);

  const { user } = useSelector(userSelector);
  const [formData, setFormData] = useState({});
  const [contactError, setContactError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const timezones = [
    { value: "IST", label: "IST" },
    { value: "UTC", label: "UTC" },
    { value: "GST", label: "GST" },
    { value: "GMT", label: "GMT" },
    { value: "EST", label: "EST" },
    { value: "CST", label: "CST" },
    { value: "PST", label: "PST" },
    { value: "IST", label: "IST" },
    { value: "BST", label: "BST" },
    { value: "AEST", label: "AEST" },
    { value: "JST", label: "JST" },
  ];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleOpenSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    setFormData({
      userid: user?.id,
      candidate_name: selectedSubmission?.candidate_name,
      candidateid: selectedSubmission?.candidate_id,
      role: selectedSubmission?.jobtitle,
      jobid: selectedSubmission?.job_id,
      candidate_email: candidateDetails?.email || "ghevin@Nityainc.com",
      clientname: selectedSubmission?.company_name,
      contactno: candidateDetails?.phone,
      interview_date: "",
      interview_time: "",
      dateforsg: "",
      enddate: "",
      notes: selectedSubmission?.notes,
      benchsales_email: user?.username,
      reason: "",
      rate: selectedSubmission?.s_rate,
      sgfromEmail: user?.username,
      status: selectedSubmission?.status,
      timeZone: "",
      interview_mode: "Offline",
      benchsales_name: user?.full_name,
    });
  }, [candidateDetails, selectedSubmission]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // Helper for field labels
  const fieldLabels = {
    candidate_name: "Candidate Name",
    role: "Position",
    clientname: "Client Name",
    interview_date: "Date Of Interview",
    timeZone: "Time Zone",
    interview_mode: "Interview Mode",
    jobid: "Job ID",
    candidate_email: "Candidate Email", // <-- Add this
  };

  // Validate a single field
  const validateField = (field, value) => {
    if (
      [
        "candidate_name",
        "role",
        "clientname",
        "interview_date",
        "timeZone",
        "interview_mode",
        "jobid",
        "candidate_email", // <-- Add this
      ].includes(field)
    ) {
      // For Date Of Interview, check for null/empty/moment invalid
      if (field === "interview_date") {
        if (!value || !moment(value).isValid()) {
          return `Please enter ${fieldLabels[field]}`;
        }
      } else if (field === "candidate_email") {
        if (!value || value === "") {
          return `Please enter ${fieldLabels[field]}`;
        }
        // Basic email format check
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return "Please enter a valid email address";
        }
      } else if (!value || value === "") {
        return `Please enter ${fieldLabels[field]}`;
      }
    }
    return "";
  };

  // onBlur handler for each field
  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleChange = (field, value) => {
    if (field === "contactno") {
      // Remove all non-numeric characters first
      const numericValue = value.replace(/[^0-9]/g, "");

      // Format the phone number as (XXX)-XXX-XXXX
      if (numericValue.length <= 10) {
        let formattedNumber = "";
        if (numericValue.length > 0) {
          formattedNumber = "(" + numericValue.slice(0, 3);
          if (numericValue.length > 3) {
            formattedNumber += ")-" + numericValue.slice(3, 6);
            if (numericValue.length > 6) {
              formattedNumber += "-" + numericValue.slice(6, 10);
            }
          }
        }
        value = formattedNumber;

        if (numericValue.length === 10) {
          setContactError("");
        } else if (numericValue.length === 0) {
          setContactError("");
        } else {
          setContactError("Please enter a valid phone number: (XXX)-XXX-XXXX");
        }
      } else {
        setContactError("Phone number should not exceed 10 digits");
        return;
      }
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      [field]: value,
    }));

    // If field was touched, validate immediately on change as well
    if (touched[field]) {
      const error = validateField(field, value);
      setFieldErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  // Add this array for required fields
  const requiredFields = [
    "candidate_name",
    "role",
    "clientname",
    "interview_date",
    "timeZone",
    "interview_mode",
    "jobid",
    "candidate_email", // <-- Add this
  ];

  // Add this function to validate all required fields on submit
  const validateAllFields = () => {
    let errors = {};
    let hasError = false;
    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
        hasError = true;
      }
    });
    setFieldErrors((prev) => ({ ...prev, ...errors }));
    setTouched((prev) => ({
      ...prev,
      ...requiredFields.reduce((acc, field) => ({ ...acc, [field]: true }), {}),
    }));
    return !hasError;
  };

  // Update handleSubmit to block submission if validation fails
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    // Validate all fields before submit
    if (!validateAllFields()) {
      setIsSubmitting(false);
      return; // Block submission if any required field is invalid
    }

    const formattedInterviewDateTime = moment(formData.interview_date).format(
      "YYYY-MM-DD"
    );

    let formattedInterviewTime = moment(formData.interview_date).format("hh:mm A");
    if (formData.timeZone) {
      formattedInterviewTime += ` ${formData.timeZone}`;
    }

    let updatedData = {
      candidateid: selectedSubmission?.candidate_id,
      jobid: selectedSubmission?.job_id,
      userid: user?.id,
      status: "8",
      nStatusName: "Interview Round-I Result", // Add this line
    };

    try {
      const response = await dispatch(
        interviewScheduleAction(
          {
            ...formData,
            interview_date: formattedInterviewDateTime,
            interview_time: formattedInterviewTime,
            companyid: user.companyid,
          },
          updatedData,
          user?.id,
          selectedSubmission?.candidate_id,
          selectedSubmission?.job_id
        )
      );

      if (response.data.affectedRows > 0) {
        handleOpenSnackbar("Interview scheduled successfully!", "success");
        // send the notification by calling the api
        const notifyData = {
          user_id: user?.id,
          notifiedto: candidateDetails?.candidate_id,
          notificationDescription: "Interview Sheduled For Your Job",
          notificationSubject: "InterviewSheduled",
          notificationType: "Candidate Notification",
          job_id: jobDetails?.id,
          candidate_name: candidateDetails?.candidate_name,
          job_title: jobDetails?.jobtitle,
        };
        await dispatch(creteNotificationAction(notifyData));
      } else {
        handleOpenSnackbar("Something went wrong!", "error");
      }
    } catch (error) {
      console.error(error);
      handleOpenSnackbar("Something went wrong!", "error");
    }

    setIsSubmitting(false);
  };

  return (
    <div
      style={{
        marginTop: "30px",
        padding: "10px",
      }}
    >
      <form onSubmit={handleSubmit}>
        <Grid
          container
          spacing={2}
          sx={{
            ".css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
              padding: "8px 6px",
            },
            ".css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input":
              { padding: "8px 6px" },
            ".css-nxo287-MuiInputBase-input-MuiOutlinedInput-input": {
              padding: "8px 6px",
            },
          }}
        >
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Candidate Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                required
                value={formData.candidate_name || ""}
                onChange={(e) => handleChange("candidate_name", e.target.value)}
                onBlur={() => handleBlur("candidate_name")}
                error={!!fieldErrors.candidate_name}
                helperText={fieldErrors.candidate_name}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Position <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                required
                value={formData.role || ""}
                onChange={(e) => handleChange("role", e.target.value)}
                onBlur={() => handleBlur("role")}
                error={!!fieldErrors.role}
                helperText={fieldErrors.role}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Job ID <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                required
                value={formData.jobid || ""}
                onChange={(e) => handleChange("jobid", e.target.value)}
                onBlur={() => handleBlur("jobid")}
                error={!!fieldErrors.jobid}
                helperText={fieldErrors.jobid}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Candidate Email <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                required
                value={formData.candidate_email || ""}
                onChange={(e) =>
                  handleChange("candidate_email", e.target.value)
                }
                onBlur={() => handleBlur("candidate_email")}
                error={!!fieldErrors.candidate_email}
                helperText={fieldErrors.candidate_email}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Client Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                required
                value={formData.clientname || ""}
                onChange={(e) => handleChange("clientname", e.target.value)}
                onBlur={() => handleBlur("clientname")}
                error={!!fieldErrors.clientname}
                helperText={fieldErrors.clientname}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Contact Number (XXX)-XXX-XXXX
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formData.contactno || ""}
                onChange={(e) => handleChange("contactno", e.target.value)}
                error={!!contactError}
                helperText={contactError}
                inputProps={{
                  inputMode: "numeric",
                }}
              />
            </FormControl>
          </Grid>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography paragraph sx={labelStyles}>
                  Date Of Interview <span style={{ color: "red" }}>*</span>
                </Typography>
                <DateTimePicker
                  fullWidth
                  inputVariant="outlined"
                  required
                  value={formData.interview_date || null}
                  onChange={(value) => handleChange("interview_date", value)}
                  slotProps={{
                    textField: {
                      onBlur: () => handleBlur("interview_date"),
                      error: !!fieldErrors.interview_date,
                      helperText: fieldErrors.interview_date,
                    },
                  }}
                  minDateTime={moment()}
                  disablePast
                />
              </FormControl>
            </Grid>
          </LocalizationProvider>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Time Zone <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                select
                variant="outlined"
                required
                value={formData.timeZone || ""}
                onChange={(e) => handleChange("timeZone", e.target.value)}
                onBlur={() => handleBlur("timeZone")}
                error={!!fieldErrors.timeZone}
                helperText={fieldErrors.timeZone}
              >
                {timezones.map((timezone, index) => (
                  <MenuItem key={index} value={timezone.value}>
                    {timezone.label}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Interview Mode <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                select
                fullWidth
                variant="outlined"
                required
                value={formData.interview_mode || ""}
                onChange={(e) => handleChange("interview_mode", e.target.value)}
                onBlur={() => handleBlur("interview_mode")}
                error={!!fieldErrors.interview_mode}
                helperText={fieldErrors.interview_mode}
              >
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="Offline">Offline</MenuItem>
              </TextField>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Notes
              </Typography>
              <TextField
                multiline
                minRows={3}
                variant="outlined"
                value={formData.notes || ""}
                onChange={(e) => handleChange("notes", e.target.value)}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ textAlign: "end" }}>
            <Button
              type="submit"
              variant="contained"
              disabled={selectedSubmission?.pStatusName === "Selected" || selectedSubmission?.pStatusName === "Rejected" || selectedSubmission?.bench_status_id !== 7}
              sx={{
                bgcolor: "#406882",
                textTransform: "capitalize",
                width: 120,
                "&:hover": { bgcolor: "#406882" },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Submit"
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

const labelStyles = {
  color: "rgb(160, 171, 184)",
  marginBottom: "5px",
};

export default Step6;
